import { Link, useGetList, useGetIdentity, useTranslate } from 'react-admin';
import { Box, Typography, Stack } from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import GroupsIcon from '@mui/icons-material/Groups';
import BugReportIcon from '@mui/icons-material/BugReport';
import CodeIcon from '@mui/icons-material/Code';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ArticleIcon from '@mui/icons-material/Article';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SchoolIcon from '@mui/icons-material/School';
import { useTheme } from '@mui/material/styles';

import { HumanDateField } from '../../common';

import { stringToColor } from '../../../services/utils';


const containerSx = {
    paddingLeft: 3,
    paddingRight: 1,
    flex: 1,
    height: '100%',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#4F51C8 transparent',
    '&::-webkit-scrollbar': {
        width: '12px'
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
        borderRadius: '12px'
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#4F51C8',
        borderRadius: '12px'
    },

    '&::-webkit-scrollbar-thumb:hover': {
        background: '#3C3EA0'
    }
};

const icons = {
    'development': CodeIcon,
    'design': PaletteIcon,
    'analysis': SearchIcon,
    'prospection': PersonSearchIcon,
    'management': GroupsIcon,
    'office': BusinessIcon,
    'communication': ChatBubbleIcon,
    'documentation': ArticleIcon,
    'meeting': GroupsIcon,
    'testing': BugReportIcon,
    'R&D': ContentPasteSearchIcon,
    'training': SchoolIcon,
    'maintenance': EngineeringIcon,
    'support': SupportAgentIcon,
    'veille': ManageSearchIcon,
    'other': DescriptionIcon
};

const formatDate = (d) => d.toISOString().split('T')[0];

const formatHours = (decimal) => {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
};

const LastTimeEntries = () => {
    const theme = useTheme();
    const translate = useTranslate();
    const { data: account } = useGetIdentity();

    const today = new Date();
    const day = today.getDay();
    const diff = (day === 0 ? -6 : 1 - day);

    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);


    const { data: lastTimeEntries } = useGetList(
        'time_entries',
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'DESC' },
            filter: { user_id: account?.id, start_date: formatDate(monday), end_date: formatDate(today) }
        }
    );

    if (!lastTimeEntries) {
        return null;
    }

    const dayEntries = lastTimeEntries?.filter(entry => entry.date === formatDate(today));
    const dayHours = dayEntries?.reduce((sum, e) => sum + e.hours, 0);
    const weekHours = lastTimeEntries?.reduce((sum, e) => sum + e.hours, 0);

    return (
        <Box sx={containerSx}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(168, 85, 247, 0.08)',
                        border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(168, 85, 247, 0.15)' : '1px solid rgba(168, 85, 247, 0.35)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            color: '#9aa0a6',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            mb: 0.5,
                        }}
                    >
                        {translate('Today')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            color: '#a855f7',
                        }}
                    >
                        {formatHours(dayHours)}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(251, 191, 36, 0.08)',
                        border: theme => theme.palette.mode === 'dark' ? '1px solid rgba(251, 191, 36, 0.15)' : '1px solid rgba(251, 191, 36, 0.55)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            color: '#9aa0a6',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            mb: 0.5,
                        }}
                    >
                        {translate('This week')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            color: '#fbbf24',
                        }}
                    >
                        {formatHours(weekHours)}
                    </Typography>
                </Box>
            </Box>
            <Stack spacing={1.5}>
                {lastTimeEntries?.map((timeEntry) => {
                    const type = timeEntry?.type?.name;
                    const project = timeEntry?.project?.name;
                    const props = { icon: icons[type] || icons['other'] };
                    const color = stringToColor(type || 'other', { dark: theme.palette.mode === 'dark' }).slice(0, -2);

                    return (
                        <Link to={`/time_entries/${timeEntry.id}`} key={timeEntry.id} sx={{ textDecoration: 'none' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: theme => theme.palette.background.paper,
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme => theme.palette.action.hover
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1.5,
                                        backgroundColor: `${color}15`,
                                    }}
                                >
                                    <props.icon sx={{ color, fontSize: 20 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            color: theme => theme.palette.text.primary,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {timeEntry.description}
                                    </Typography>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '0.7rem',
                                                color: theme => theme.palette.text.secondary,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            {project}
                                        </Typography>
                                    </Box>
                                    <HumanDateField
                                        source="created_at"
                                        record={timeEntry}
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: theme => theme.palette.text.secondary,
                                            mt: 0.25,
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: timeEntry.color,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {timeEntry.pretty_hours}
                                </Typography>
                            </Box>
                        </Link>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default LastTimeEntries;
